import React, { FC, useMemo } from 'react'
import { graphql, useFragment } from 'react-relay'
import { useScript } from 'hoofd'
import { formatInTimeZone } from 'date-fns-tz'
import { remark } from 'remark'
import strip from 'strip-markdown'

import { Metadata } from '../Metadata'

import { EventMetadata_event$key } from './__generated__/EventMetadata_event.graphql'

import { HOST_NAME } from '@data/config/url'
import { EMOJI_REGEX } from '@data/config/regex'

export const EventMetadata: FC<{
  event: EventMetadata_event$key
  title?: string
  description?: string
}> = ({ event, title, description }) => {
  const data = useFragment(
    graphql`
      fragment EventMetadata_event on Event {
        name
        prettyUrl
        description
        coverPhoto {
          url
        }

        startAt
        endAt
        timeZone

        hasExternalUrl
        hasVenue

        externalUrl
        seoVenue {
          name
          address {
            addressLine1
            addressLine2
            city
            stateProvinceRegionCode
            zipPostalCode
            countryCode
          }
        }

        updatedAt

        owner {
          __typename

          ... on User {
            firstName
            lastName
            slugId

            isMe
            primaryPhoto {
              url
            }
          }

          ... on Guild {
            name
            slugId

            myMembership {
              membershipLevel
            }
            primaryPhoto {
              url
            }
          }
        }

        seoPresentations: presentations(first: 10) {
          edges {
            node {
              title
              prettyUrl

              description

              presenter {
                slugId
                firstName
                lastName

                primaryPhoto {
                  url
                }
              }

              presenterFirstName
              presenterLastName
            }
          }
        }
      }
    `,
    event
  )

  const strippedDescription = description ? description : data.description

  // const strippedDescription = useMemo(() => {
  //   if (description) {
  //     return String(remark().use(strip).processSync(description))

  //   }

  //   if (data.description) {
  //     return String(remark().use(strip).processSync(data.description))

  //   }

  //   return null
  // }, [description, data.description])

  const dateTime = useMemo(
    () =>
      formatInTimeZone(new Date(data.startAt), data.timeZone, 'MMM do h:mma'),
    [data.startAt, data.timeZone]
  )

  const dateTimeDescription = useMemo(
    () =>
      strippedDescription
        ? `${dateTime}: ${strippedDescription.replace(EMOJI_REGEX, '')}`
        : dateTime,
    [dateTime, strippedDescription]
  )

  const structuredData = useMemo(() => {
    const venue = data.hasVenue
      ? {
          '@type': 'Place',
          name: data.seoVenue?.name,
          telephone: '', // needed for a Safari bug...
          address: {
            '@type': 'PostalAddress',
            streetAddress: data.seoVenue?.address?.addressLine1,
            addressLocality: data.seoVenue?.address?.city,
            postalCode: data.seoVenue?.address?.zipPostalCode,
            addressRegion: data.seoVenue?.address?.stateProvinceRegionCode,
            addressCountry: data.seoVenue?.address?.countryCode,
          },
        }
      : null

    const performer = data.seoPresentations.edges.map((edge) => {
      if (edge.node.presenter) {
        return {
          '@type': 'Person',
          // givenName: edge.node.presenter.firstName,
          // familyName: edge.node.presenter.lastName,
          name: `${edge.node.presenter.firstName} ${edge.node.presenter.lastName}`,
          url: `${HOST_NAME}/${edge.node.presenter.slugId || ''}`,
          image: edge.node.presenter.primaryPhoto?.url
            ? `${edge.node.presenter.primaryPhoto?.url}?w=500&h=500&fit=contain`
            : null,
        }
      }

      return {
        '@type': 'Person',
        // givenName: edge.node.presenterFirstName,
        // familyName: edge.node.presenterLastName,
        name: `${edge.node.presenterFirstName} ${edge.node.presenterLastName}`,
      }
    })

    // const workPerformed = data.seoPresentations.edges.map((edge) => {
    //   const author = edge.node.presenter
    //     ? {
    //         '@type': 'Person',
    //         givenName: edge.node.presenter.firstName,
    //         familyName: edge.node.presenter.lastName,
    //         name: `${edge.node.presenter.firstName} ${edge.node.presenter.lastName}`,
    //         url: `${HOST_NAME}/${edge.node.presenter.slugId || ''}`,
    //         image: edge.node.presenter.primaryPhoto?.url
    //           ? `${edge.node.presenter.primaryPhoto?.url}?w=500&h=500&fit=contain`
    //           : null,
    //       }
    //     : {
    //         '@type': 'Person',
    //         givenName: edge.node.presenterFirstName,
    //         familyName: edge.node.presenterLastName,
    //         name: `${edge.node.presenterFirstName} ${edge.node.presenterLastName}`,
    //       }

    //   return {
    //     '@type': 'CreativeWork',
    //     name: edge.node.title,
    //     url: `${HOST_NAME}/presentations/${edge.node.prettyUrl}`,
    //     author,
    //     description: edge.node.description
    //       ? String(remark().use(strip).processSync(edge.node.description))
    //       : null,
    //   }
    // })

    return JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Event',
      name: data.name,
      description: strippedDescription,
      url: `${HOST_NAME}/events/${data.prettyUrl}`,
      organizer:
        data.owner?.__typename === 'User'
          ? {
              '@type': 'Person',
              givenName: data.owner.firstName,
              familyName: data.owner.lastName,
              name: `${data.owner.firstName} ${data.owner.lastName}`,
              url: `${HOST_NAME}/${data.owner?.slugId || ''}`,
              image: data.owner.primaryPhoto?.url
                ? `${data.owner.primaryPhoto?.url}?w=500&h=500&fit=contain`
                : null,
            }
          : data.owner?.__typename === 'Guild'
          ? {
              '@type': 'Organization',
              name: data.owner.name || 'Guild',
              url: `${HOST_NAME}/${data.owner.slugId || ''}`,
              logo: data.owner.primaryPhoto?.url
                ? `${data.owner.primaryPhoto?.url}?w=500&h=500&fit=contain`
                : null,
            }
          : null,
      startDate: data.startAt,
      endDate: data.endAt,
      eventAttendanceMode:
        data.hasExternalUrl && data.hasVenue
          ? 'https://schema.org/MixedEventAttendanceMode'
          : data.hasVenue
          ? 'https://schema.org/OfflineEventAttendanceMode'
          : 'https://schema.org/OnlineEventAttendanceMode',
      eventStatus: 'https://schema.org/EventScheduled',
      location:
        data.hasExternalUrl && data.hasVenue
          ? [{ '@type': 'VirtualLocation', url: data.externalUrl }, venue]
          : data.hasVenue
          ? venue
          : { '@type': 'VirtualLocation', url: data.externalUrl },
      image: [
        `${HOST_NAME}/social/event/${data.prettyUrl}/card.png?${Math.floor(
          Date.now() / (60 * 60 * 1000)
        )}-${data.updatedAt}`,
        data.coverPhoto?.url,
      ].filter(Boolean),
      offers: null,
      performer,
      // workPerformed,
    })
  }, [data, strippedDescription])

  useScript({
    // id: 'event-structured-data',
    type: 'application/ld+json',
    text: structuredData,
  })

  return (
    <Metadata
      description={description || dateTimeDescription}
      // imageTransformParams={data.coverPhoto?.url ? 'FULL_SIZE' : 'NONE'}
      imageTransformParams='NONE'
      photoUrl={
        // data.coverPhoto?.url ||
        `${HOST_NAME}/social/event/${data.prettyUrl}/card.png?${Math.floor(
          Date.now() / (60 * 60 * 1000)
        )}-${data.updatedAt}`
      }
      title={title || data.name}
    />
  )
}
